import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Package,
    Loader2,
    AlertCircle,
    Check,
    Shield,
    Sparkles,
    Calendar,
    Tag,
    Wrench,
    MessageSquare,
    Mail,
} from "lucide-react";
import { API_BASE_URL } from "../config/api";

// ─── Types ──────────────────────────────────────────────────────────────────

type ClaimStatus =
    | "REQUESTED"
    | "ACCEPTED_PICKUP"
    | "PRE_QC"
    | "IN_SERVICE"
    | "POST_QC"
    | "DISPATCHED"
    | "DELIVERED"
    | "REJECTED"
    | "CANCELLED";

interface ClaimData {
    id: number;
    bookingRef: string;
    pickupDate: string;
    category: string;
    subService: string;
    comments: string | null;
    claimStatus: ClaimStatus | null;
    createdAt: string;
    updatedAt: string | null;
}

interface SaleData {
    id: number;
    uniqueId: string;
    productName: string;
    customerName: string | null;
    brand: string | null;
    purchaseDate: string;
    carePrice: string;
    saleStatus: string;
}

interface TrackingResponse {
    sale: SaleData | null;
    claims: ClaimData[];
    totalClaims: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (s: string | null) => {
    if (!s) return "—";
    const d = new Date(s);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}, ${d.getFullYear()}`;
};

const fmtDateTime = (s: string | null) => {
    if (!s) return "—";
    const d = new Date(s);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}, ${d.getFullYear()} · ${displayHours}:${minutes} ${ampm}`;
};

// ─── Timeline Steps (visible to customer — 5 steps, no PRE_QC / POST_QC) ───

interface TimelineStep {
    key: string;
    label: string;
    description: string;
    visibleStepNumber: number; // 1–5 for display
    icon: React.FC<{ className?: string }>;
}

const TIMELINE_STEPS: TimelineStep[] = [
    {
        key: "REQUESTED",
        label: "Request Received",
        description: "Your service request has been received and confirmed.",
        visibleStepNumber: 1,
        icon: Shield,
    },
    {
        key: "ACCEPTED_PICKUP",
        label: "Accepted & Pickup Scheduled",
        description: "Your request has been approved. Our team will schedule the pickup.",
        visibleStepNumber: 2,
        icon: Check,
    },
    {
        key: "IN_SERVICE",
        label: "Service In Progress",
        description: "Your product has been received and is being serviced by our specialist team.",
        visibleStepNumber: 3,
        icon: Wrench,
    },
    {
        key: "DISPATCHED",
        label: "Return Dispatched",
        description: "Your item has been serviced, quality-checked, and is on its way back to you.",
        visibleStepNumber: 4,
        icon: Package,
    },
    {
        key: "DELIVERED",
        label: "Delivered to Customer",
        description: "Your product has been delivered. We hope you love it!",
        visibleStepNumber: 5,
        icon: Sparkles,
    },
];

// ─── Internal ordering (all 7 statuses for progress math) ───────────────────

const INTERNAL_ORDER: Record<string, number> = {
    REQUESTED: 1,
    ACCEPTED_PICKUP: 2,
    PRE_QC: 3,
    IN_SERVICE: 4,
    POST_QC: 5,
    DISPATCHED: 6,
    DELIVERED: 7,
};

const getInternalStep = (status: ClaimStatus | null | undefined): number => {
    if (!status) return 0;
    if (status === "REJECTED" || status === "CANCELLED") return -1;
    return INTERNAL_ORDER[status] ?? 0;
};

/**
 * Map the real (internal 1-7) status to the visible step (1-5).
 */
const getVisibleStep = (status: ClaimStatus | null | undefined): number => {
    switch (status) {
        case "REQUESTED":
            return 1;
        case "ACCEPTED_PICKUP":
            return 2;
        case "PRE_QC":
            return 2; // hidden — maps into step 2
        case "IN_SERVICE":
            return 3;
        case "POST_QC":
            return 3; // hidden — maps into step 3
        case "DISPATCHED":
            return 4;
        case "DELIVERED":
            return 5;
        case "REJECTED":
        case "CANCELLED":
            return -1;
        default:
            return 0;
    }
};

/**
 * Progress percentage — smooth across all 7 internal stages
 */
const getProgressPercent = (status: ClaimStatus | null | undefined): number => {
    switch (status) {
        case "REQUESTED":
            return 0;
        case "ACCEPTED_PICKUP":
            return 10;
        case "PRE_QC":
            return 25;
        case "IN_SERVICE":
            return 45;
        case "POST_QC":
            return 65;
        case "DISPATCHED":
            return 85;
        case "DELIVERED":
            return 100;
        default:
            return 0;
    }
};

const getStatusLabel = (status: ClaimStatus | null | undefined): string => {
    switch (status) {
        case "REQUESTED":
            return "Request Received";
        case "ACCEPTED_PICKUP":
            return "Accepted & Pickup";
        case "PRE_QC":
            return "Processing";
        case "IN_SERVICE":
            return "In Service";
        case "POST_QC":
            return "Finishing Up";
        case "DISPATCHED":
            return "Dispatched";
        case "DELIVERED":
            return "Delivered";
        case "REJECTED":
            return "Rejected";
        case "CANCELLED":
            return "Cancelled";
        default:
            return "Unknown";
    }
};

/**
 * Mapping of visible step → highest internal stage that belongs to it:
 *   visible 1 (REQUESTED)       → internal 1
 *   visible 2 (ACCEPTED_PICKUP) → internal 3  (includes PRE_QC)
 *   visible 3 (IN_SERVICE)      → internal 5  (includes POST_QC)
 *   visible 4 (DISPATCHED)      → internal 6
 *   visible 5 (DELIVERED)       → internal 7
 */
const VISIBLE_STEP_MAX_INTERNAL: Record<number, number> = {
    1: 1,
    2: 3, // ACCEPTED_PICKUP + PRE_QC
    3: 5, // IN_SERVICE + POST_QC
    4: 6,
    5: 7,
};

const VISIBLE_STEP_MIN_INTERNAL: Record<number, number> = {
    1: 1,
    2: 2,
    3: 4,
    4: 6,
    5: 7,
};

type StepState = "completed" | "current" | "future";

const getStepState = (visibleStep: number, status: ClaimStatus | null | undefined): StepState => {
    const internal = getInternalStep(status);
    if (internal <= 0) return "future"; // rejected / cancelled / unknown

    // If status is DELIVERED or completed, all steps (1 to 5) are marked as DONE
    if (status === "DELIVERED" || internal >= 7) {
        return "completed";
    }

    const maxInternal = VISIBLE_STEP_MAX_INTERNAL[visibleStep];
    const minInternal = VISIBLE_STEP_MIN_INTERNAL[visibleStep];

    if (internal > maxInternal) return "completed";
    if (internal >= minInternal) return "current";
    return "future";
};

// ─── Main Component ─────────────────────────────────────────────────────────

const ClaimTracker: React.FC = () => {
    const { bookingRef } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [trackingData, setTrackingData] = useState<TrackingResponse | null>(null);

    useEffect(() => {
        if (!bookingRef) {
            setError("No Service Request ID provided");
            setLoading(false);
            return;
        }

        const fetchStatus = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await fetch(`${API_BASE_URL}/api/claim/track/${bookingRef}`);
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(data.message || "No records found");
                }
                setTrackingData(await res.json());
            } catch (err: any) {
                setError(err.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
    }, [bookingRef]);

    // ── Loading ──
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-blue-600/10 rounded-2xl flex items-center justify-center border border-blue-500/20">
                        <Loader2 className="w-7 h-7 animate-spin text-blue-400" />
                    </div>
                    <div className="text-center">
                        <p className="text-base text-white font-medium">Loading service request</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Error ──
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center px-4">
                <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1e293b] rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
                    <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-red-500/20">
                        <AlertCircle className="w-8 h-8 text-red-400" />
                    </div>
                    <p className="text-xl text-white font-semibold mb-2">Service Request Not Found</p>
                    <p className="text-sm text-gray-400 mb-6 leading-relaxed">{error}</p>
                    <button
                        onClick={() => navigate("/")}
                        className="w-full px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
                    >
                        Return Home
                    </button>
                </div>
            </div>
        );
    }

    // ── No Data ──
    if (!trackingData || trackingData.claims.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center px-4">
                <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1e293b] rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
                    <div className="w-16 h-16 bg-gray-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-gray-500/20">
                        <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-xl text-white font-semibold mb-2">No Service Request Found</p>
                    <p className="text-sm text-gray-400 leading-relaxed">
                        Please use the link from your confirmation email or WhatsApp message
                    </p>
                </div>
            </div>
        );
    }

    const claim = trackingData.claims[0];
    const sale = trackingData.sale;
    const visibleStep = getVisibleStep(claim.claimStatus);
    const progressPercent = getProgressPercent(claim.claimStatus);
    const isRejectedOrCancelled = visibleStep === -1;
    const isCompleted = claim.claimStatus === "DELIVERED";

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617]">
            {/* ── Header ── */}
            <div className="bg-[#0f172a]/80 backdrop-blur-xl border-b border-[#1e293b]/50 sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <Package className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <span className="text-sm font-bold text-white tracking-wider">CARE+ SERVICE</span>
                            <p className="text-xs text-gray-500">by Secured Stitch</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
                {/* ── Service Request Header ── */}
                <div className="flex items-end justify-between">
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-1.5">
                            Service Request
                        </p>
                        <h1 className="text-3xl font-extrabold text-white tracking-tight">{claim.id}</h1>
                    </div>
                    {!isRejectedOrCancelled && (
                        <div
                            className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                                isCompleted
                                    ? "bg-green-500/15 text-green-400 border border-green-500/25"
                                    : "bg-blue-500/15 text-blue-400 border border-blue-500/25"
                            }`}
                        >
                            {getStatusLabel(claim.claimStatus)}
                        </div>
                    )}
                </div>

                {/* ── Info Cards ── */}
                <div className="grid grid-cols-3 gap-2.5">
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl px-3.5 py-3.5">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Tag className="w-3.5 h-3.5 text-blue-400" />
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Category</p>
                        </div>
                        <p className="text-sm text-white font-semibold leading-tight">{claim.category}</p>
                    </div>
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl px-3.5 py-3.5">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Wrench className="w-3.5 h-3.5 text-purple-400" />
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Service</p>
                        </div>
                        <p className="text-sm text-white font-semibold leading-tight">{claim.subService || "—"}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-600/10 to-blue-700/5 border border-blue-500/20 rounded-2xl px-3.5 py-3.5">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Calendar className="w-3.5 h-3.5 text-blue-400" />
                            <p className="text-xs text-blue-300/70 uppercase tracking-wider font-semibold">Pickup</p>
                        </div>
                        <p className="text-sm text-blue-200 font-semibold leading-tight">{fmt(claim.pickupDate)}</p>
                    </div>
                </div>

                {/* ── Step Indicators (5 visible steps) ── */}
                {!isRejectedOrCancelled && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 uppercase tracking-widest font-semibold mr-1">Step</span>
                        {[1, 2, 3, 4, 5].map((step) => {
                            const state = getStepState(step, claim.claimStatus);
                            return (
                                <div
                                    key={step}
                                    className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                                        state === "completed"
                                            ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                            : state === "current"
                                              ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30 scale-110"
                                              : "bg-[#1e293b]/60 text-gray-600 border border-[#2d3748]/50"
                                    }`}
                                >
                                    {state === "completed" ? "✓" : step}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Progress Bar ── */}
                {!isRejectedOrCancelled && (
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-base font-bold text-white">Progress</span>
                            <span
                                className={`text-xl font-extrabold ${isCompleted ? "text-green-400" : "text-blue-400"}`}
                            >
                                {progressPercent}%
                            </span>
                        </div>
                        <div className="w-full h-3 bg-[#1e293b] rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-1000 ease-out ${
                                    isCompleted
                                        ? "bg-gradient-to-r from-green-500 to-emerald-400"
                                        : "bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400"
                                }`}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-3 font-medium">
                            <span>Request Received</span>
                            <span>Completed</span>
                        </div>
                    </div>
                )}

                {/* ── Rejected / Cancelled Banner ── */}
                {isRejectedOrCancelled && (
                    <div
                        className={`rounded-2xl p-5 border backdrop-blur-sm ${
                            claim.claimStatus === "REJECTED"
                                ? "bg-red-500/5 border-red-500/20"
                                : "bg-gray-500/5 border-gray-500/20"
                        }`}
                    >
                        <div className="flex items-start gap-4">
                            <div
                                className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                                    claim.claimStatus === "REJECTED" ? "bg-red-500/15" : "bg-gray-500/15"
                                }`}
                            >
                                <AlertCircle
                                    className={`w-6 h-6 ${
                                        claim.claimStatus === "REJECTED" ? "text-red-400" : "text-gray-400"
                                    }`}
                                />
                            </div>
                            <div>
                                <p
                                    className={`text-lg font-bold ${
                                        claim.claimStatus === "REJECTED" ? "text-red-400" : "text-gray-400"
                                    }`}
                                >
                                    Service Request {claim.claimStatus === "REJECTED" ? "Rejected" : "Cancelled"}
                                </p>
                                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                    {claim.claimStatus === "REJECTED"
                                        ? "Your service request has been reviewed and could not be approved at this time."
                                        : "This service request has been cancelled. Contact us if you need assistance."}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Timeline (5 visible steps) ── */}
                <div className="space-y-0">
                    {TIMELINE_STEPS.map((step, index) => {
                        const state = getStepState(step.visibleStepNumber, claim.claimStatus);
                        const isStepCompleted = state === "completed";
                        const isCurrent = state === "current";
                        const isFuture = state === "future";
                        const isLast = index === TIMELINE_STEPS.length - 1;
                        const StepIcon = step.icon;

                        if (isRejectedOrCancelled && step.visibleStepNumber > 1) return null;

                        return (
                            <div key={step.key} className="flex gap-4">
                                {/* Dot + Line */}
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                                            isStepCompleted
                                                ? "bg-green-500/15 text-green-400 border border-green-500/25 shadow-lg shadow-green-500/10"
                                                : isCurrent
                                                  ? "bg-blue-600 text-white shadow-xl shadow-blue-500/30 ring-4 ring-blue-500/15"
                                                  : "bg-[#1e293b]/50 text-gray-600 border border-[#2d3748]/40"
                                        }`}
                                    >
                                        {isStepCompleted ? (
                                            <Check className="w-5 h-5" strokeWidth={3} />
                                        ) : isCurrent ? (
                                            <StepIcon className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-bold">{step.visibleStepNumber}</span>
                                        )}
                                    </div>
                                    {!isLast && (
                                        <div className="w-0.5 flex-1 min-h-[24px] my-1">
                                            <div
                                                className={`w-full h-full rounded-full transition-all duration-700 ${
                                                    isStepCompleted ? "bg-green-500/40" : "bg-[#1e293b]/60"
                                                }`}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div
                                    className={`pb-4 flex-1 transition-all duration-500 ${isFuture ? "opacity-30" : ""}`}
                                >
                                    <div
                                        className={`rounded-2xl p-4 transition-all duration-500 ${
                                            isCurrent
                                                ? "bg-blue-600/8 border border-blue-500/20 shadow-lg shadow-blue-500/5"
                                                : isStepCompleted
                                                  ? "bg-[#111827]/50 border border-[#1e293b]/50"
                                                  : "bg-transparent border border-transparent"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <h3
                                                className={`text-base font-bold ${
                                                    isCurrent
                                                        ? "text-white"
                                                        : isStepCompleted
                                                          ? "text-gray-200"
                                                          : "text-gray-500"
                                                }`}
                                            >
                                                {step.label}
                                            </h3>
                                            {isStepCompleted && (
                                                <span className="text-xs font-bold text-green-400 bg-green-500/10 px-2.5 py-1 rounded-lg border border-green-500/20 uppercase tracking-wider">
                                                    Done
                                                </span>
                                            )}
                                            {isCurrent && (
                                                <span className="text-xs font-bold text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-lg border border-blue-500/20 uppercase tracking-wider animate-pulse">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                        <p
                                            className={`text-sm leading-relaxed ${
                                                isCurrent
                                                    ? "text-blue-200/70"
                                                    : isStepCompleted
                                                      ? "text-gray-400"
                                                      : "text-gray-600"
                                            }`}
                                        >
                                            {step.description}
                                        </p>
                                        {(isStepCompleted || isCurrent) && (
                                            <p
                                                className={`text-xs mt-2.5 font-medium ${
                                                    isCurrent ? "text-blue-300/50" : "text-gray-500/70"
                                                }`}
                                            >
                                                {isCurrent
                                                    ? fmtDateTime(claim.updatedAt || claim.createdAt)
                                                    : fmtDateTime(claim.createdAt)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── Product Info ── */}
                {sale && (
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Package className="w-4 h-4 text-gray-500" />
                            <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Product Details</p>
                        </div>
                        <div className="space-y-3.5">
                            {[
                                { label: "Product", value: sale.productName },
                                { label: "Brand", value: sale.brand || "—" },
                                { label: "Purchase Date", value: fmt(sale.purchaseDate) },
                                { label: "Care+ ID", value: String(sale.id), mono: true },
                            ].map(({ label, value, mono }) => (
                                <div key={label} className="flex justify-between items-start">
                                    <span className="text-sm text-gray-500 font-medium">{label}</span>
                                    <span
                                        className={`text-sm text-white text-right max-w-[55%] font-semibold ${
                                            mono ? "font-mono" : ""
                                        }`}
                                    >
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Comments ── */}
                {claim.comments && (
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquare className="w-4 h-4 text-gray-500" />
                            <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Customer Notes</p>
                        </div>
                        <p className="text-sm text-gray-300 leading-relaxed">{claim.comments}</p>
                    </div>
                )}

                {/* ── Footer ── */}
                <div className="text-center pt-6 pb-10 space-y-3">
                    <div className="w-11 h-11 bg-[#1e293b]/50 rounded-2xl flex items-center justify-center mx-auto">
                        <Mail className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Need help with your service request?</p>
                        <a
                            href="mailto:care@securedstitch.com"
                            className="text-sm text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                        >
                            contact@securedstitch.com
                        </a>
                    </div>
                    <p className="text-xs text-gray-700 font-medium">
                        © {new Date().getFullYear()} Secured Stitch. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ClaimTracker;